import { useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, AlertCircle } from 'lucide-react';
import { useSportsStore } from '@/store/useSportsStore';
import type { WizardStepProps, SportSlice } from '../../../engine/types';
import type { Sport } from '@/types/sports.model';
import { hasSportFlow } from '../../';
import { useWizard } from '../../../engine';
import { getFlowForSport } from '../../';
import { cn } from '@/lib/utils';

export function SportStep({ data, updateData: _updateData }: WizardStepProps<SportSlice>) {
  const { setStepData, changeFlow, draft: _draft } = useWizard();

  // Sports store
  const sportsData = useSportsStore((s) => s.sportsData);
  const isLoading = useSportsStore((s) => s.isLoading);
  const isInitialized = useSportsStore((s) => s.isInitialized);
  const fetchSports = useSportsStore((s) => s.fetchSports);
  const error = useSportsStore((s) => s.error);

  // Fetch sports on mount
  const handleFetchSports = useCallback(() => {
    if (!isInitialized) {
      fetchSports();
    }
  }, [isInitialized, fetchSports]);

  useEffect(() => {
    handleFetchSports();
  }, [handleFetchSports]);

  // Handle sport selection
  const handleSelectSport = (sport: Sport) => {
    // Check if sport has a wizard flow
    if (!hasSportFlow(sport.uid)) {
      return; // Button should be disabled, but double-check
    }

    // Update the draft with selected sport
    setStepData('sport', { sport });

    // Get the flow for the selected sport and update the flow ID
    const newFlow = getFlowForSport(sport.uid);
    if (newFlow) {
      // Notify parent about flow change - this will trigger navigation
      changeFlow(newFlow.id, true);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <p className="mt-4 text-sm text-gray-600">Cargando deportes...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="rounded-full bg-red-100 p-3">
          <AlertCircle className="h-6 w-6 text-red-600" />
        </div>
        <p className="mt-4 text-sm text-red-600">{error}</p>
        <button
          onClick={handleFetchSports}
          className="mt-4 text-sm text-blue-600 hover:underline"
        >
          Reintentar
        </button>
      </div>
    );
  }

  // Empty state
  if (sportsData.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <p className="text-gray-500">No hay deportes disponibles</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {sportsData.map(({ sport }) => {
          const isSelected = data?.sport?.uid === sport.uid;
          const hasFlow = hasSportFlow(sport.uid);

          return (
            <SportCard
              key={sport.uid}
              sport={sport}
              isSelected={isSelected}
              hasFlow={hasFlow}
              onSelect={() => handleSelectSport(sport)}
            />
          );
        })}
      </div>
    </div>
  );
}

// ============================================================================
// SPORT CARD
// ============================================================================

interface SportCardProps {
  sport: Sport;
  isSelected: boolean;
  hasFlow: boolean;
  onSelect: () => void;
}

function SportCard({ sport, isSelected, hasFlow, onSelect }: SportCardProps) {
  // Sport emoji mapping
  const sportEmojis: Record<string, string> = {
    padel: '🎾',
    tennis: '🎾',
    squash: '🎾',
    futbol: '⚽',
    basketball: '🏀',
  };

  const emoji = sportEmojis[sport.uid.toLowerCase()] ?? '🏆';

  return (
    <Card
      className={cn(
        'relative cursor-pointer transition-all duration-200',
        isSelected
          ? 'border-2 border-blue-500 bg-blue-50 ring-2 ring-blue-200'
          : hasFlow
            ? 'border-gray-200 hover:border-blue-300 hover:shadow-md'
            : 'cursor-not-allowed border-gray-100 bg-gray-50 opacity-60',
      )}
      onClick={hasFlow ? onSelect : undefined}
    >
      <CardContent className="flex flex-col items-center p-6">
        {/* Emoji */}
        <span className="text-4xl">{emoji}</span>

        {/* Name */}
        <h3
          className={cn(
            'mt-3 text-lg font-semibold',
            isSelected ? 'text-blue-700' : 'text-gray-900',
          )}
        >
          {sport.name}
        </h3>

        {/* Coming soon badge */}
        {!hasFlow && (
          <Badge variant="secondary" className="mt-2 bg-gray-200 text-gray-600">
            Próximamente
          </Badge>
        )}

        {/* Selected indicator */}
        {isSelected && (
          <div className="absolute right-2 top-2">
            <div className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-500">
              <svg
                className="h-4 w-4 text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default SportStep;
